import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/auth.store';
import Layout from './components/Layout';
import DriverLayout from './components/DriverLayout';
import CookieBanner from './components/CookieBanner';
import AnalyticsTracker from './components/AnalyticsTracker';
import UpdatePrompt from './components/UpdatePrompt';
import InactivityLogoutWatcher from './components/InactivityLogoutWatcher';
import MerchantDemoGate from './components/MerchantDemoGate';
import { Loader2 } from 'lucide-react';
import { RouteSeo, HtmlLangSync, SkipToContent } from './seo';
import './i18n';

// Lazy loading pour optimiser le bundle
const LandingPage            = lazy(() => import('./pages/LandingPage'));
const MerchantOnboardingPage = lazy(() => import('./pages/onboarding/MerchantOnboardingPage'));
const LoginPage          = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage       = lazy(() => import('./pages/auth/RegisterPage'));
const VerifyEmailPage    = lazy(() => import('./pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage  = lazy(() => import('./pages/auth/ResetPasswordPage'));
const DashboardPage      = lazy(() => import('./pages/dashboard/DashboardPage'));
const OrdersPage         = lazy(() => import('./pages/orders/OrdersPage'));
const ProductsPage       = lazy(() => import('./pages/products/ProductsPage'));
const StatsPage          = lazy(() => import('./pages/stats/StatsPage'));
const SettingsPage       = lazy(() => import('./pages/settings/SettingsPage'));
const HomePage           = lazy(() => import('./pages/client/HomePage'));
const ShopPage           = lazy(() => import('./pages/client/ShopPage'));
const ClientOrdersPage   = lazy(() => import('./pages/client/ClientOrdersPage'));
const ClientProfilePage  = lazy(() => import('./pages/client/ClientProfilePage'));
const AdminPage          = lazy(() => import('./pages/admin/AdminPage'));
const AdminProfilePage   = lazy(() => import('./pages/admin/AdminProfilePage'));
const DriverPage           = lazy(() => import('./pages/driver/DriverPage'));
const DriverProfilePage    = lazy(() => import('./pages/driver/DriverProfilePage'));
const DevTestAccountsPage = lazy(() => import('./pages/dev/DevTestAccountsPage'));
const MerchantDemoCategoryPage = lazy(() => import('./pages/demo/MerchantDemoCategoryPage'));
const PrivacyPage         = lazy(() => import('./pages/legal/PrivacyPage'));
const TermsPage           = lazy(() => import('./pages/legal/TermsPage'));
const ImpressumPage       = lazy(() => import('./pages/legal/ImpressumPage'));
const CgvPage             = lazy(() => import('./pages/legal/CgvPage'));
const MerchantTermsPage   = lazy(() => import('./pages/legal/MerchantTermsPage'));
const MerchantSupportPage = lazy(() => import('./pages/support/MerchantSupportPage'));
const MerchantGuidePage   = lazy(() => import('./pages/support/MerchantGuidePage'));
const InstallApkGuidePage = lazy(() => import('./pages/support/InstallApkGuidePage'));
const SupportPage         = lazy(() => import('./pages/support/SupportPage'));
const NotFoundPage        = lazy(() => import('./pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 size={32} className="animate-spin text-primary-700" />
    </div>
  );
}

function ProtectedRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { isAuthenticated, user } = useAuthStore();
  // État incohérent : marqué connecté mais pas de données utilisateur → logout silencieux
  if (isAuthenticated && !user) return <Navigate to="/login" replace />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && user && !roles.includes(user.role)) {
    if (user.role === 'driver') return <Navigate to="/driver" replace />;
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'customer') return <Navigate to="/" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SkipToContent />
        <HtmlLangSync />
        <RouteSeo />
        <Suspense fallback={<PageLoader />}>
          <AnalyticsTracker />
          <CookieBanner />
          <UpdatePrompt />
          <InactivityLogoutWatcher />
          <Routes>
            {/* ── Landing page publique ── */}
            <Route path="/landing" element={<LandingPage />} />

            {/* ── Onboarding commerçant ── accessible directement après inscription */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <MerchantOnboardingPage />
              </ProtectedRoute>
            } />

            {/* ── Routes publiques ── */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/registrieren" element={<Navigate to="/register" replace />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Comptes de test (dev / staging si VITE_SHOW_TEST_ACCOUNTS=true) */}
            <Route path="/dev/comptes-test" element={<DevTestAccountsPage />} />
            <Route path="/demo/commerce" element={
              <ProtectedRoute roles={['merchant']}>
                <MerchantDemoCategoryPage />
              </ProtectedRoute>
            } />
            <Route path="/demo/handel" element={<Navigate to="/demo/commerce" replace />} />

            {/* ── Application Client (publique + connecté) ── */}
            <Route path="/client"        element={<HomePage />} />
            <Route path="/shop/:shopSlug" element={<ShopPage />} />
            <Route path="/client/orders"   element={<ClientOrdersPage />} />
            <Route path="/client/profile"  element={<ClientProfilePage />} />

            {/* Accueil vitrine : pas de redirection auto vers /dashboard (évite 401 → /login si session périmée). */}
            <Route path="/" element={<HomePage />} />

            {/* ── Dashboard commerçant / admin : route SANS path sur le parent pour ne JAMAIS
                voler le match « / » (sinon ProtectedRoute renvoie les visiteurs vers /login). ── */}
            <Route element={
              <ProtectedRoute roles={['merchant', 'admin']}>
                <MerchantDemoGate />
              </ProtectedRoute>
            }>
              <Route element={<Layout />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="orders"    element={<OrdersPage />} />
                <Route path="products"  element={<ProductsPage />} />
                <Route path="stats"     element={<StatsPage />} />
                <Route path="settings"  element={<SettingsPage />} />
                <Route path="assistance" element={<MerchantSupportPage />} />
                <Route path="assistance/:guideId" element={<MerchantGuidePage />} />
              </Route>
            </Route>

            {/* ── Interface Admin ── */}
            <Route path="/admin" element={
              <ProtectedRoute roles={['admin']}>
                <Layout adminMode />
              </ProtectedRoute>
            }>
              <Route index element={<AdminPage />} />
              <Route path="profile" element={<AdminProfilePage />} />
            </Route>

            {/* ── Interface Livreur ── */}
            <Route path="/driver" element={
              <ProtectedRoute roles={['driver', 'admin']}>
                <DriverLayout />
              </ProtectedRoute>
            }>
              <Route index element={<DriverPage />} />
              <Route path="profile" element={<DriverProfilePage />} />
            </Route>

            {/* ── Pages légales nLPD / RGPD ── */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms"   element={<TermsPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/cgv" element={<CgvPage />} />
            <Route path="/merchant-terms" element={<MerchantTermsPage />} />
            <Route path="/support" element={<SupportPage />} />

            <Route path="/app-android" element={
              <ProtectedRoute roles={['merchant', 'driver', 'admin']}>
                <InstallApkGuidePage />
              </ProtectedRoute>
            }             />

            {/* Fallback 404 (noindex) — pas de redirect accueil (soft-404) */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
